/**
 * MultiDealNegotiationDashboard Component
 * Manage multiple simultaneous negotiations with bulk actions and prioritization
 *
 * Ed Dua requirement (March 9, 2026): "Volume of offers" - handle many negotiations at once
 */

import { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Mail,
  Clock,
  Search,
  Download,
  ChevronRight,
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import type {
  MultiDealNegotiationSummary,
  NegotiationStageType,
  PosturingIndicatorType,
} from '@/types/negotiation';
import {
  negotiationStageLabels,
  negotiationStageColors,
  posturingIndicatorLabels,
  posturingIndicatorColors,
  posturingIndicatorIcons,
  NegotiationStage,
  PosturingIndicator,
} from '@/types/negotiation';

// ============================================================================
// MOCK DATA (Replace with API calls in production)
// ============================================================================

const MOCK_MULTI_DEAL_SUMMARIES: MultiDealNegotiationSummary[] = [
  {
    deal_id: '1',
    deal_name: 'Sequoia Capital Fund VIII',
    seller_name: 'John Seller',
    stage: NegotiationStage.PRICING_DISCUSSION,
    posturing_indicator: PosturingIndicator.COOPERATIVE,
    last_contact_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    next_action_date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
    days_since_last_contact: 3,
    response_rate: 80,
    target_value: 5000000,
    headline_discount: 0.15,
  },
  {
    deal_id: '2',
    deal_name: 'Andreessen Horowitz Fund V',
    seller_name: 'Sarah Smith',
    stage: NegotiationStage.COUNTER_OFFER,
    posturing_indicator: PosturingIndicator.RESISTANT,
    last_contact_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    next_action_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    days_since_last_contact: 7,
    response_rate: 50,
    target_value: 3500000,
    headline_discount: 0.20,
  },
  {
    deal_id: '3',
    deal_name: 'Accel Partners Fund XII',
    seller_name: 'Mike Johnson',
    stage: NegotiationStage.FIRST_RESPONSE,
    posturing_indicator: PosturingIndicator.RECEPTIVE,
    last_contact_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    next_action_date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
    days_since_last_contact: 1,
    response_rate: 100,
    target_value: 2000000,
    headline_discount: 0.10,
  },
  {
    deal_id: '4',
    deal_name: 'Benchmark Capital Fund IX',
    seller_name: 'Lisa Anderson',
    stage: NegotiationStage.INITIAL_OUTREACH,
    posturing_indicator: PosturingIndicator.SILENT,
    last_contact_date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    next_action_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    days_since_last_contact: 14,
    response_rate: 0,
    target_value: 4000000,
    headline_discount: 0.18,
  },
  {
    deal_id: '5',
    deal_name: 'Kleiner Perkins Fund XVII',
    seller_name: 'Robert Chen',
    stage: NegotiationStage.ACCEPTANCE,
    posturing_indicator: PosturingIndicator.COOPERATIVE,
    last_contact_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    next_action_date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
    days_since_last_contact: 2,
    response_rate: 90,
    target_value: 6000000,
    headline_discount: 0.12,
  },
  {
    deal_id: '6',
    deal_name: 'Greylock Partners Fund XV',
    seller_name: 'Emily Davis',
    stage: NegotiationStage.DUE_DILIGENCE_REQUEST,
    posturing_indicator: PosturingIndicator.STALLING,
    last_contact_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    next_action_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    days_since_last_contact: 5,
    response_rate: 60,
    target_value: 2500000,
    headline_discount: 0.22,
  },
];

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface MultiDealNegotiationDashboardProps {
  onDealClick?: (dealId: string) => void;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function MultiDealNegotiationDashboard({
  onDealClick,
  className,
}: MultiDealNegotiationDashboardProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<NegotiationStageType | 'all'>('all');
  const [posturingFilter, setPosturingFilter] = useState<PosturingIndicatorType | 'all'>('all');
  const [sortBy, setSortBy] = useState<'priority' | 'value' | 'days_since_contact'>('priority');

  // Filter and sort deals
  const filteredDeals = useMemo(() => {
    let filtered = MOCK_MULTI_DEAL_SUMMARIES.filter((deal) => {
      const matchesSearch =
        searchQuery === '' ||
        deal.deal_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.seller_name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStage = stageFilter === 'all' || deal.stage === stageFilter;
      const matchesPosturing =
        posturingFilter === 'all' || deal.posturing_indicator === posturingFilter;

      return matchesSearch && matchesStage && matchesPosturing;
    });

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'priority') {
        // Priority: response_rate high, days_since_contact low, receptive/cooperative posturing
        const aPriority =
          a.response_rate * 0.4 -
          (a.days_since_last_contact ?? 0) * 2 +
          (a.posturing_indicator === PosturingIndicator.RECEPTIVE ||
          a.posturing_indicator === PosturingIndicator.COOPERATIVE
            ? 20
            : 0);
        const bPriority =
          b.response_rate * 0.4 -
          (b.days_since_last_contact ?? 0) * 2 +
          (b.posturing_indicator === PosturingIndicator.RECEPTIVE ||
          b.posturing_indicator === PosturingIndicator.COOPERATIVE
            ? 20
            : 0);
        return bPriority - aPriority;
      } else if (sortBy === 'value') {
        return b.target_value - a.target_value;
      } else {
        // days_since_contact
        return (b.days_since_last_contact ?? 0) - (a.days_since_last_contact ?? 0);
      }
    });

    return filtered;
  }, [searchQuery, stageFilter, posturingFilter, sortBy]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = filteredDeals.length;
    const totalValue = filteredDeals.reduce((sum, deal) => sum + deal.target_value, 0);
    const needsAttention = filteredDeals.filter(
      (d) =>
        d.posturing_indicator === PosturingIndicator.SILENT ||
        d.posturing_indicator === PosturingIndicator.RESISTANT ||
        (d.days_since_last_contact ?? 0) > 7
    ).length;
    const hot = filteredDeals.filter(
      (d) =>
        d.posturing_indicator === PosturingIndicator.RECEPTIVE ||
        d.posturing_indicator === PosturingIndicator.COOPERATIVE
    ).length;

    return { total, totalValue, needsAttention, hot };
  }, [filteredDeals]);

  const getPriorityColor = (deal: MultiDealNegotiationSummary): string => {
    if (
      deal.posturing_indicator === PosturingIndicator.RECEPTIVE ||
      deal.posturing_indicator === PosturingIndicator.COOPERATIVE
    ) {
      return 'border-l-green-500';
    }
    if (
      deal.posturing_indicator === PosturingIndicator.SILENT ||
      deal.posturing_indicator === PosturingIndicator.RESISTANT ||
      (deal.days_since_last_contact ?? 0) > 7
    ) {
      return 'border-l-red-500';
    }
    return 'border-l-yellow-500';
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Multi-Deal Negotiation Dashboard</h2>
          <p className="text-gray-600 mt-1">Manage volume negotiations across your pipeline</p>
        </div>
        <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export Report
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Active Negotiations</p>
            <Mail className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{summaryStats.total}</p>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Total Pipeline Value</p>
            <TrendingUp className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {formatCurrency(summaryStats.totalValue)}
          </p>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Hot Deals</p>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600 mt-2">{summaryStats.hot}</p>
        </div>

        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Needs Attention</p>
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-red-600 mt-2">{summaryStats.needsAttention}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search deals or sellers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as NegotiationStageType | 'all')}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Stages</option>
            {Object.entries(negotiationStageLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={posturingFilter}
            onChange={(e) =>
              setPosturingFilter(e.target.value as PosturingIndicatorType | 'all')
            }
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Posturing</option>
            {Object.entries(posturingIndicatorLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as 'priority' | 'value' | 'days_since_contact')
            }
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="priority">Sort by Priority</option>
            <option value="value">Sort by Value</option>
            <option value="days_since_contact">Sort by Last Contact</option>
          </select>
        </div>
      </div>

      {/* Deals Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Posturing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDeals.map((deal) => (
                <tr
                  key={deal.deal_id}
                  className={cn(
                    'hover:bg-gray-50 transition-colors border-l-4',
                    getPriorityColor(deal)
                  )}
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{deal.deal_name}</p>
                      <p className="text-sm text-gray-600">{deal.seller_name}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full border',
                        negotiationStageColors[deal.stage]
                      )}
                    >
                      {negotiationStageLabels[deal.stage]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {posturingIndicatorIcons[deal.posturing_indicator]}
                      </span>
                      <span
                        className={cn(
                          'px-2 py-1 text-xs font-medium rounded-full border',
                          posturingIndicatorColors[deal.posturing_indicator]
                        )}
                      >
                        {posturingIndicatorLabels[deal.posturing_indicator]}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span
                        className={cn(
                          'text-sm',
                          (deal.days_since_last_contact ?? 0) > 7
                            ? 'text-red-600 font-medium'
                            : 'text-gray-900'
                        )}
                      >
                        {deal.days_since_last_contact}d ago
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {deal.response_rate >= 70 ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                      <span
                        className={cn(
                          'text-sm font-medium',
                          deal.response_rate >= 70 ? 'text-green-600' : 'text-red-600'
                        )}
                      >
                        {deal.response_rate}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(deal.target_value)}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => onDealClick?.(deal.deal_id)}
                      className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm font-medium"
                    >
                      View
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredDeals.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No negotiations found matching your filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
